import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import {
  checkAppVersion,
  DEFAULT_RESULT,
  type AppVersionCheckResult,
} from "@/services/appVersionService";

/**
 * Runs the app-version check on mount and whenever the app returns from
 * background (e.g. user reopens the app after an App Store update). On web
 * or if anything fails, status stays "ok" so this never blocks the app.
 */
export const useAppVersionCheck = (): AppVersionCheckResult => {
  const [result, setResult] = useState<AppVersionCheckResult>(DEFAULT_RESULT);

  useEffect(() => {
    let cancelled = false;
    let listener: PluginListenerHandle | undefined;

    const run = async () => {
      const r = await checkAppVersion();
      if (!cancelled) setResult(r);
    };

    void run();

    if (Capacitor.isNativePlatform()) {
      // Re-check when the app returns to the foreground; if the user just
      // updated via the App Store / Play Store and reopened, this clears
      // the banner without needing a full restart.
      void CapApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive && !cancelled) void run();
      }).then((handle) => {
        if (cancelled) {
          // Cleanup ran before registration completed — remove now so the
          // listener doesn't leak into the global App plugin.
          void handle.remove();
        } else {
          listener = handle;
        }
      });
    }

    return () => {
      cancelled = true;
      void listener?.remove();
    };
  }, []);

  return result;
};
